import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { Eye, EyeOff, Save } from "lucide-react";

export default function StaffGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/staff/gallery");
      setItems(data);
      const d = {};
      data.forEach((it) => { d[it.id] = { title: it.title, caption: it.caption, src: it.src, order: it.order }; });
      setDraft(d);
    } catch { toast.error("Could not load gallery."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const update = (id, key, val) => setDraft((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val } }));

  const save = async (id) => {
    try {
      const payload = draft[id];
      const { data } = await api.patch(`/staff/gallery/${id}`, {
        ...payload,
        order: Number(payload.order),
      });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const toggle = async (item) => {
    try {
      const { data } = await api.patch(`/staff/gallery/${item.id}`, { active: !item.active });
      setItems((prev) => prev.map((it) => (it.id === item.id ? data : it)));
      toast.success(data.active ? "Now visible on site" : "Hidden");
    } catch { toast.error("Toggle failed"); }
  };

  return (
    <div data-testid="staff-gallery">
      <div className="eyebrow">Gallery</div>
      <h1 className="mt-2 text-4xl">Photos on the /gallery page</h1>
      <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
        Edit titles and captions, toggle visibility, or reorder using the "order" number.
        Images use paths under <code>/images/</code> (paste an external URL if you need to swap).
      </p>

      {loading && <div className="mt-8 text-[hsl(var(--ink-soft))]">Loading...</div>}

      <div className="mt-8 space-y-4">
        {items.map((it) => {
          const d = draft[it.id] || {};
          const dirty =
            d.title !== it.title || d.caption !== it.caption || d.src !== it.src || Number(d.order) !== it.order;
          return (
            <div key={it.id} data-testid={`gallery-row-${it.id}`} className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4 grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-4 items-start">
              <img src={it.src} alt={it.title} className="w-full h-24 object-cover" />
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                <label className="sm:col-span-3 text-xs">
                  <span className="uppercase tracking-wider text-[hsl(var(--ink-soft))]">Title</span>
                  <input
                    className="mt-1 w-full bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] rounded px-3 py-2 text-sm"
                    value={d.title || ""} onChange={(e) => update(it.id, "title", e.target.value)}
                  />
                </label>
                <label className="sm:col-span-2 text-xs">
                  <span className="uppercase tracking-wider text-[hsl(var(--ink-soft))]">Image path / URL</span>
                  <div className="mt-1 flex gap-2">
                    <input
                      className="flex-1 bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] rounded px-3 py-2 text-sm font-mono"
                      value={d.src || ""} onChange={(e) => update(it.id, "src", e.target.value)}
                    />
                    <ImageUploader
                      testid={`gallery-upload-${it.id}`}
                      label=""
                      onUploaded={(url) => update(it.id, "src", url)}
                    />
                  </div>
                </label>
                <label className="text-xs">
                  <span className="uppercase tracking-wider text-[hsl(var(--ink-soft))]">Order</span>
                  <input
                    type="number"
                    className="mt-1 w-full bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] rounded px-3 py-2 text-sm"
                    value={d.order ?? 0} onChange={(e) => update(it.id, "order", e.target.value)}
                  />
                </label>
                <label className="sm:col-span-6 text-xs">
                  <span className="uppercase tracking-wider text-[hsl(var(--ink-soft))]">Caption</span>
                  <textarea
                    rows={2}
                    className="mt-1 w-full bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] rounded px-3 py-2 text-sm resize-none"
                    value={d.caption || ""} onChange={(e) => update(it.id, "caption", e.target.value)}
                  />
                </label>
              </div>
              <div className="flex md:flex-col gap-2">
                <button
                  data-testid={`gallery-save-${it.id}`}
                  onClick={() => save(it.id)}
                  disabled={!dirty}
                  className="inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))] disabled:opacity-40"
                >
                  <Save size={12} /> Save
                </button>
                <button
                  data-testid={`gallery-toggle-${it.id}`}
                  onClick={() => toggle(it)}
                  className="inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))]"
                >
                  {it.active ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
